import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Wrench, Save, Eye, X, Loader2, AlertCircle, CheckCircle2, CalendarClock,
  Power, Clock, MessageCircle, Info,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { MaintenancePage } from '../../maintenance/MaintenancePage';
import {
  MAINTENANCE_TABLE,
  PengaturanMaintenance,
  StatusMaintenance,
  DEFAULT_JUDUL,
  DEFAULT_PESAN,
  getStatusMaintenance,
  formatWaktuWIB,
  isoKeInputLokal,
  inputLokalKeIso,
  normalisasiNomorWa,
} from '../../../utils/maintenance';

type ModeMulai = 'sekarang' | 'terjadwal';

interface FormState {
  aktif: boolean;
  modeMulai: ModeMulai;
  mulai: string;
  selesai: string;
  selesaiOtomatis: boolean;
  judul: string;
  pesan: string;
  kontakWa: string;
}

const formAwal: FormState = {
  aktif: false,
  modeMulai: 'sekarang',
  mulai: '',
  selesai: '',
  selesaiOtomatis: false,
  judul: DEFAULT_JUDUL,
  pesan: DEFAULT_PESAN,
  kontakWa: '',
};

const keForm = (p: PengaturanMaintenance | null): FormState => {
  if (!p) return formAwal;
  return {
    aktif: p.aktif,
    modeMulai: p.mulai ? 'terjadwal' : 'sekarang',
    mulai: isoKeInputLokal(p.mulai),
    selesai: isoKeInputLokal(p.selesai),
    selesaiOtomatis: p.selesai_otomatis,
    judul: p.judul || DEFAULT_JUDUL,
    pesan: p.pesan || DEFAULT_PESAN,
    kontakWa: p.kontak_wa || '',
  };
};

const INFO_STATUS: Record<StatusMaintenance, { label: string; desc: string; kelas: string }> = {
  nonaktif: { label: 'Nonaktif', desc: 'Aplikasi berjalan normal untuk semua pengguna.', kelas: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  terjadwal: { label: 'Terjadwal', desc: 'Aplikasi masih normal. Pengguna melihat pengumuman 24 jam sebelum mulai.', kelas: 'bg-blue-50 text-blue-700 ring-blue-200' },
  berlangsung: { label: 'Sedang berlangsung', desc: 'Semua pengguna melihat halaman maintenance, kecuali Super Admin.', kelas: 'bg-amber-50 text-amber-700 ring-amber-200' },
  berakhir: { label: 'Sudah berakhir', desc: 'Waktu selesai sudah lewat, aplikasi kembali normal. Matikan saklar untuk merapikan.', kelas: 'bg-gray-100 text-gray-700 ring-gray-200' },
};

const pesanError = (e: any): string => {
  const code = e?.code || '';
  if (code === '42P01' || code === 'PGRST205') {
    return 'Tabel pengaturan_maintenance belum ada. Jalankan SQL supabase/migrations/20261027060000_pengaturan_maintenance.sql di Supabase SQL Editor.';
  }
  if (code === '42501') return 'Ditolak database. Hanya Super Admin yang bisa mengubah pengaturan ini. Coba keluar lalu masuk lagi.';
  return e?.message || 'Terjadi kesalahan.';
};

const PortalGuruPengaturanMaintenance: React.FC = () => {
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const [tersimpan, setTersimpan] = useState<PengaturanMaintenance | null>(null);
  const [form, setForm] = useState<FormState>(formAwal);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pesan, setPesan] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pratinjau, setPratinjau] = useState(false);
  const [konfirmasi, setKonfirmasi] = useState(false);

  const muat = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from(MAINTENANCE_TABLE).select('*').eq('id', 1).maybeSingle();
      if (error) throw error;
      setTersimpan(data as PengaturanMaintenance | null);
      setForm(keForm(data as PengaturanMaintenance | null));
    } catch (e) {
      setPesan({ type: 'error', text: pesanError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { muat(); }, []);

  const ubah = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const dataBaru: PengaturanMaintenance = useMemo(() => ({
    id: 1,
    aktif: form.aktif,
    mulai: form.modeMulai === 'terjadwal' ? inputLokalKeIso(form.mulai) : null,
    selesai: inputLokalKeIso(form.selesai),
    selesai_otomatis: form.selesaiOtomatis && !!form.selesai,
    judul: form.judul.trim() || DEFAULT_JUDUL,
    pesan: form.pesan.trim() || DEFAULT_PESAN,
    kontak_wa: form.kontakWa.trim() || null,
    updated_by: authUser?.email ?? null,
    updated_at: tersimpan?.updated_at ?? null,
  }), [form, authUser, tersimpan]);

  const statusSekarang = getStatusMaintenance(tersimpan);
  const statusBaru = getStatusMaintenance(dataBaru);

  const validasi = (): string | null => {
    if (form.modeMulai === 'terjadwal' && !dataBaru.mulai) return 'Isi waktu mulai, atau pilih "Mulai sekarang".';
    if (dataBaru.mulai && dataBaru.selesai && new Date(dataBaru.selesai) <= new Date(dataBaru.mulai)) {
      return 'Perkiraan selesai harus setelah waktu mulai.';
    }
    if (form.aktif && form.selesaiOtomatis && dataBaru.selesai && new Date(dataBaru.selesai) <= new Date()) {
      return 'Perkiraan selesai sudah lewat. Dengan "Selesai otomatis", maintenance langsung berakhir. Ubah waktunya dulu.';
    }
    if (form.kontakWa.trim() && !normalisasiNomorWa(form.kontakWa)) return 'Nomor WhatsApp tidak valid.';
    return null;
  };

  const simpan = async () => {
    setKonfirmasi(false);
    const err = validasi();
    if (err) { setPesan({ type: 'error', text: err }); return; }
    setSaving(true);
    setPesan(null);
    try {
      const { updated_at: _abaikan, ...baris } = dataBaru;
      const { data, error } = await supabase.from(MAINTENANCE_TABLE).upsert(baris, { onConflict: 'id' }).select().maybeSingle();
      if (error) throw error;
      if (!data) throw { code: '42501' };
      setTersimpan(data as PengaturanMaintenance);
      setForm(keForm(data as PengaturanMaintenance));
      const st = getStatusMaintenance(data as PengaturanMaintenance);
      setPesan({
        type: 'success',
        text: st === 'berlangsung'
          ? 'Tersimpan. Mode Maintenance sekarang AKTIF untuk semua pengguna selain Super Admin.'
          : st === 'terjadwal'
            ? `Tersimpan. Maintenance dijadwalkan mulai ${formatWaktuWIB((data as PengaturanMaintenance).mulai)}.`
            : 'Tersimpan. Aplikasi berjalan normal.',
      });
    } catch (e) {
      setPesan({ type: 'error', text: pesanError(e) });
    } finally {
      setSaving(false);
    }
  };

  const klikSimpan = () => {
    const err = validasi();
    if (err) { setPesan({ type: 'error', text: err }); return; }
    // Konfirmasi hanya saat penyimpanan ini langsung mengunci pengguna.
    if (statusBaru === 'berlangsung' && statusSekarang !== 'berlangsung') setKonfirmasi(true);
    else simpan();
  };

  const info = INFO_STATUS[statusSekarang];

  return (
    <div className="min-h-screen bg-gray-50 pb-24 font-sans">
      <div className="sticky top-0 z-30 border-b bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate('/portal-guru/admin/pengaturan')} className="rounded-full p-2 transition-colors hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold leading-tight text-gray-900">Mode Maintenance</h1>
            <p className="text-xs font-medium text-gray-500">Pengaturan Sistem · Super Admin</p>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-4 max-w-3xl space-y-4 p-4">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand-primary" /></div>
        ) : (
          <>
            {/* Status */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-amber-50 p-2.5"><Wrench className="h-6 w-6 text-amber-600" /></div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Status saat ini</p>
                    <span className={`mt-1 inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ring-1 ${info.kelas}`}>{info.label}</span>
                    <p className="mt-1 text-xs text-gray-500">{info.desc}</p>
                  </div>
                </div>
                {tersimpan?.updated_at && (
                  <p className="text-[11px] text-gray-400 sm:text-right">
                    Diubah {formatWaktuWIB(tersimpan.updated_at)}
                    {tersimpan.updated_by ? <><br />oleh {tersimpan.updated_by}</> : null}
                  </p>
                )}
              </div>
            </div>

            {/* Saklar */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <label className="flex cursor-pointer items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Power className={`mt-0.5 h-5 w-5 ${form.aktif ? 'text-amber-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-bold text-gray-900">Aktifkan Mode Maintenance</p>
                    <p className="text-xs leading-relaxed text-gray-500">
                      Saat berlangsung, semua halaman (login, Portal Guru, Portal Wali, kiosk, dan halaman publik) diganti halaman maintenance.
                      Hanya akun Super Admin yang bisa masuk normal.
                    </p>
                  </div>
                </div>
                <span className="relative inline-flex shrink-0">
                  <input type="checkbox" className="peer sr-only" checked={form.aktif} onChange={(e) => ubah('aktif', e.target.checked)} />
                  <span className="h-7 w-12 rounded-full bg-gray-200 transition peer-checked:bg-amber-500" />
                  <span className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
                </span>
              </label>
            </div>

            {/* Jadwal */}
            <div className="space-y-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="flex items-center gap-2 font-bold text-gray-900"><CalendarClock className="h-5 w-5 text-brand-primary" /> Jadwal</p>

              <div>
                <p className="mb-2 text-xs font-semibold text-gray-600">Kapan mulai?</p>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { v: 'sekarang', t: 'Mulai sekarang', d: 'Langsung saat disimpan' },
                    { v: 'terjadwal', t: 'Jadwalkan', d: 'Mulai di waktu tertentu' },
                  ] as const).map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => ubah('modeMulai', o.v)}
                      className={`rounded-lg border p-3 text-left transition ${form.modeMulai === o.v ? 'border-brand-primary bg-brand-light ring-1 ring-brand-primary' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <p className="text-sm font-bold text-gray-900">{o.t}</p>
                      <p className="text-[11px] text-gray-500">{o.d}</p>
                    </button>
                  ))}
                </div>
                {form.modeMulai === 'terjadwal' && (
                  <input
                    type="datetime-local"
                    value={form.mulai}
                    onChange={(e) => ubah('mulai', e.target.value)}
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                  />
                )}
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold text-gray-600">Perkiraan selesai <span className="font-normal text-gray-400">(opsional)</span></p>
                <p className="mb-2 text-[11px] text-gray-500">Ditampilkan sebagai hitung mundur di halaman maintenance.</p>
                <div className="flex gap-2">
                  <input
                    type="datetime-local"
                    value={form.selesai}
                    onChange={(e) => ubah('selesai', e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                  />
                  {form.selesai && (
                    <button type="button" onClick={() => ubah('selesai', '')} className="shrink-0 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                      Hapus
                    </button>
                  )}
                </div>
                <label className={`mt-3 flex items-start gap-2.5 ${form.selesai ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                  <input
                    type="checkbox"
                    disabled={!form.selesai}
                    checked={form.selesaiOtomatis && !!form.selesai}
                    onChange={(e) => ubah('selesaiOtomatis', e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-800">Selesai otomatis</span>
                    <span className="block text-[11px] leading-relaxed text-gray-500">
                      Aplikasi terbuka sendiri saat waktu selesai tiba. Bila tidak dicentang, maintenance berjalan sampai saklar dimatikan.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* Isi halaman */}
            <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="flex items-center gap-2 font-bold text-gray-900"><Info className="h-5 w-5 text-brand-primary" /> Isi halaman</p>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">Judul</label>
                <input
                  value={form.judul}
                  maxLength={80}
                  onChange={(e) => ubah('judul', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">Pesan untuk pengguna</label>
                <textarea
                  value={form.pesan}
                  rows={4}
                  maxLength={500}
                  onChange={(e) => ubah('pesan', e.target.value)}
                  className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
                <p className="mt-1 text-right text-[11px] text-gray-400">{form.pesan.length}/500</p>
              </div>
              <div>
                <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                  <MessageCircle className="h-3.5 w-3.5" /> Nomor WhatsApp admin <span className="font-normal text-gray-400">(opsional)</span>
                </label>
                <input
                  value={form.kontakWa}
                  inputMode="tel"
                  placeholder="08xxxxxxxxxx"
                  onChange={(e) => ubah('kontakWa', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
                <p className="mt-1 text-[11px] text-gray-500">Bila diisi, muncul tombol "Hubungi Admin" di halaman maintenance.</p>
              </div>
            </div>

            {pesan && (
              <div className={`flex items-start gap-2 rounded-xl p-3 text-sm ${pesan.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
                {pesan.type === 'success' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}
                <span>{pesan.text}</span>
              </div>
            )}

            <div className="rounded-xl bg-blue-50 p-3 text-xs leading-relaxed text-blue-800">
              <p className="mb-1 flex items-center gap-1.5 font-bold"><Clock className="h-3.5 w-3.5" /> Setelah disimpan</p>
              Aplikasi yang sedang terbuka di HP pengguna berganti dalam beberapa detik (paling lambat 1 menit). Selama maintenance, Anda tetap bisa memakai
              aplikasi seperti biasa dan melihat tombol oranye "Maintenance aktif" di pojok kiri bawah untuk kembali ke halaman ini.
            </div>
          </>
        )}
      </div>

      {/* Tombol bawah */}
      {!loading && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t bg-white/95 p-3 backdrop-blur">
          <div className="mx-auto flex max-w-3xl gap-2">
            <button
              type="button"
              onClick={() => setPratinjau(true)}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50"
            >
              <Eye className="h-4 w-4" /> Pratinjau
            </button>
            <button
              type="button"
              onClick={klikSimpan}
              disabled={saving}
              className="flex flex-[2] items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-sm font-bold text-white hover:bg-brand-secondary disabled:opacity-70"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
            </button>
          </div>
        </div>
      )}

      {/* Pratinjau */}
      {pratinjau && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-3 sm:p-6" onClick={() => setPratinjau(false)}>
          <div className="relative mx-auto max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between text-white">
              <p className="text-sm font-bold">Pratinjau halaman maintenance</p>
              <button onClick={() => setPratinjau(false)} className="rounded-full bg-white/10 p-1.5 hover:bg-white/20" aria-label="Tutup pratinjau">
                <X className="h-5 w-5" />
              </button>
            </div>
            <MaintenancePage pengaturan={dataBaru} pratinjau />
          </div>
        </div>
      )}

      {/* Konfirmasi aktif sekarang */}
      {konfirmasi && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-amber-100"><Wrench className="h-6 w-6 text-amber-600" /></div>
            <h3 className="text-lg font-bold text-gray-900">Aktifkan maintenance sekarang?</h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              Semua pengguna selain Super Admin langsung tidak bisa memakai aplikasi, termasuk presensi dan kiosk, sampai maintenance dimatikan
              {dataBaru.selesai_otomatis && dataBaru.selesai ? ` atau sampai ${formatWaktuWIB(dataBaru.selesai)}` : ''}.
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setKonfirmasi(false)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50">Batal</button>
              <button onClick={simpan} className="flex-1 rounded-xl bg-amber-500 py-2.5 text-sm font-bold text-white hover:bg-amber-600">Ya, aktifkan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalGuruPengaturanMaintenance;
